import React, { useState, useRef, useEffect, useCallback } from 'react';
import PQueue from 'p-queue';

// If a click is made outside the component, then the function onOutsideClick is called.
// This function returns a ref which needs to be assigned to the component.

const useOutsideClick = (onOutsideClick) => {
  const componentRef = useRef();
  const handleOutsideClick = (e) => {
    if (!onOutsideClick || componentRef.current.contains(e.target)) {
      return;
    }
    onOutsideClick();
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => { document.removeEventListener("mousedown", handleOutsideClick); };
  });
  return componentRef;
};

// This hooks handles batch of asynchronous requests, and provide everything
// the NekoProgress might need.

const isDebug = false;
const UploadQueue = new PQueue({ concurrency: 1, autoStart: false });

const useNekoTasks = () => {

  // We should be able to kill fetch requests using this
  // Interesting article: https://davidwalsh.name/cancel-fetch
  const [ controller, setController ] = useState(new AbortController());
  const refErrorsCount = useRef(0);
  const refSkip = useRef(false);
  const [ busy, setBusy ] = useState(false);
  const [ paused, setPaused ] = useState(false);
  const [ max, setMax ] = useState(0);
  const [ errorCount, setErrorCount ] = useState(0);
  const [ error, setError ] = useState(false);
  const [ success, setSuccess ] = useState(false);
  const [ value, setValue ] = useState(0);

  const reset = async () => {
    setError(false);
    setErrorCount(0);
    refSkip.current = false;
    setPaused(false);
    setSuccess(false);
    setValue(0);
  };

  const start = async (tasks) => {
    setError(false);
    setErrorCount(0);
    refSkip.current = false;
    setPaused(false);
    setSuccess(false);
    setTasks(tasks);
    setBusy(true);
    if (isDebug) { console.log('Started process.'); }
    UploadQueue.start();
    UploadQueue.onIdle().then(onIdle); 
  };

  const pause = async () => {
    if (isDebug) { console.log('Paused process.'); }
    UploadQueue.pause();
    setPaused(true);
  };

  const resume = async () => {
    if (isDebug) { console.log('Resume process.'); }
    setError(false);
    setPaused(false);
    UploadQueue.start();
  };

  const stop = async () => {
    if (isDebug) { console.log('Cancelled process.'); }
    UploadQueue.pause();
    controller.abort();
    setBusy(false);
    setError(false);
    setSuccess(false);
  };

  const onIdle = async () => {
    if (isDebug) { console.log('onIdle.'); }
    setSuccess(true);
    setBusy(false);
  };

  const setTasks = async (tasks) => {
    let newController = new AbortController();
    setController(newController);
    UploadQueue.clear();
    for (let task of tasks) {
      UploadQueue.add(async () => {
        try {
          let res = await task(newController.signal);
          if (!res.success) {
            setErrorCount(errorCount => errorCount + 1);
            console.log('Error in UploadQueue', res);
            if (!refSkip.current) {
              setError(res.error);
              pause();
            }
          }
        }
        catch (err) {
          if (err.name === 'AbortError') {
            console.log('Aborted');
            return;
          }
          else {
            setErrorCount(errorCount => errorCount + 1);
            console.log('Error in UploadQueue', err);
            if (!refSkip.current) {
              setError("An error was caught (more information in the console).");
              pause();
            }
          }
        }
        finally {
          setValue(value => value + 1);
        }
      });
    }
    setValue(0);
    setMax(tasks.length);
  };

  const setAlwaysSkip = useCallback((value = true) => {
    refSkip.current = true;
  }, []);

  const getErrorCount = () => {
    return errorCount;
  };

  return { start, stop, pause, resume, reset, error, success, busy, paused, value, max, setAlwaysSkip, getErrorCount };
};

export { useOutsideClick, useNekoTasks };